// Where the app gets its words + phonic breakdown + audio locations.
//
// Leave `sheetId` empty to use the built-in demo words (works offline, no setup).
// To drive everything from your Google Sheet instead:
//   1. Build the sheet from the template in  sheet-template/  (one row per sound).
//   2. Share it: "Anyone with the link → Viewer"  (no need to publish).
//   3. Copy the id from the sheet URL:
//        https://docs.google.com/spreadsheets/d/<THIS_IS_THE_ID>/edit#gid=<TAB_GID>
//   4. Paste the id (and the tab's gid) below, then rebuild.
//
// The audio "location" cells can be Google Drive share links — the app converts
// them to direct links automatically. If a recording can't be reached (offline
// or blocked), the app falls back to the bundled placeholder sound.

export const SHEET = {
  sheetId: '',   // e.g. '1AbCdEfGhIjKlMnOpQrStUvWxYz0123456789'
  gid: '0',      // the tab's gid (0 for the first tab)
}

// Extension of the bundled fallback audio in public/audio/.
export const AUDIO_EXT = 'wav'

// Where the recorded MP3s live. The sheet holds only words + sounds now; audio is
// found BY NAME using this convention:
//   whole word   -> <word>.mp3            (e.g. cat.mp3)
//   each sound N  -> <word>-<N>.mp3        (e.g. cat-1.mp3, cat-2.mp3, cat-3.mp3)
// Two ways to point at the files (fill ONE):
//
// 1) A Google Drive folder (needs a free Google API key with the Drive API on).
//    Share the folder "Anyone with the link", then set the folder id + key here.
export const DRIVE_AUDIO = {
  folderId: '', // the id in the folder URL: drive.google.com/drive/folders/<THIS>
  apiKey: '',   // a Google Cloud API key with "Google Drive API" enabled
}
//
// 2) OR a plain name-addressable base URL (no key needed) — your website or a
//    public bucket, e.g. 'https://aaviond.com/phonic-audio/'. Files are fetched as
//    <base>/<word>-<N>.mp3. Leave '' to use the Drive folder above instead.
export const AUDIO_BASE_URL = ''

// 3) LOCAL folder (default, great for localhost): drop files in  public/phonics/
//    and the app serves them from here until you set one of the two online
//    options above. Phoneme files: <code>.mp3 ; whole words: words/<word>.mp3
export const PHONEME_BASE = 'phonics/'
export const PHONEME_EXT = 'mp3'

// Bottom-slide "word" blend tuning. The engine trims the silence off each phoneme
// clip and plays them tightly so they run together into a word. Turn these up to
// compress more:
//   overlap 0..0.5  — how much each sound overlaps the next (higher = tighter)
//   rate    ~1..1.4 — playback speed (higher = faster/shorter; slight pitch rise)
//   silence 0..0.05 — amplitude below this (0-1) is treated as silence to trim
// overlap 0..0.5  — how much each sound overlaps the next
// rate            — playback speed for CONSONANTS (compressed/snappy)
// vowelRate       — playback speed for VOWELS (~1.0 = natural, sustained)
// stopDur/consDur — max seconds for stop / other consonants (vowels play full)
// silence         — amplitude treated as silence to trim
export const BLEND = { overlap: 0.2, rate: 1.15, vowelRate: 1.0, stopDur: 0.14, consDur: 0.26, silence: 0.01 }

// Where in-app word recordings + ratings are saved. This app is served by its
// own Rails backend, which exposes the endpoints below at the SAME origin:
//   POST /api/upload?word=<id>&slot=<1-3>&ext=<webm|m4a|…>  (raw audio body)
//   POST /api/rate                                          (JSON rating)
// Same-origin relative paths work everywhere the Rails app runs: localhost,
// the tablet over Wi‑Fi (http://<pc-ip>:3000), and the deployed site. If a
// request fails, the app keeps the data on-device and re-syncs later.
export const UPLOAD_URL = '/api/upload'
export const RATE_URL = '/api/rate'

// Direct link to the installable Android app (APK). Shown as a "Get the app"
// button in the BROWSER version only (hidden inside the native app). Point it at
// wherever you host the APK (your website, a shared Drive link, a GitHub
// release), or keep the default and put the APK in public/ as PhonicSound.apk.
export const APK_URL = '/PhonicSound.apk'
